/**
 * Image Carousel Module
 * Handles auto-scrolling carousel with pause/resume on click and pagination
 *
 * Contract:
 * - Pure component module: no auto-boot, no globals
 * - Safe in shared bundles: caller decides where to initialize
 * - Idempotent per container element
 */

class ImageCarousel {
    /**
     * @param {string|Element} containerOrSelector - DOM element or selector string
     * @param {object} [options]
     * @param {number} [options.autoScrollDuration=3000] - milliseconds
     */
    constructor(containerOrSelector, options = {}) {
        const container =
            typeof containerOrSelector === 'string'
                ? document.querySelector(containerOrSelector)
                : containerOrSelector;

        if (!container) return;

        // Prevent double-init on the same element
        if (container.dataset.carouselInitialized === 'true') return;
        container.dataset.carouselInitialized = 'true';

        this.container = container;
        this.images = Array.from(this.container.querySelectorAll('.carousel-image'));
        this.track = this.container.querySelector('.carousel-track');
        this.paginationContainer = this.container.querySelector('.carousel-pagination');

        this.currentIndex = 0;
        this.isAutoScrolling = true;
        this.autoScrollInterval = null;
        this.autoScrollDuration =
            typeof options.autoScrollDuration === 'number'
                ? options.autoScrollDuration
                : 3000;

        this.onTrackClick = this.toggleAutoScroll.bind(this);

        this.init();
    }

    init() {
        if (!this.images.length) return;

        this.createPagination();

        if (this.track) {
            this.track.addEventListener('click', this.onTrackClick);
        }

        this.startAutoScroll();
    }

    createPagination() {
        if (!this.paginationContainer) return;

        this.paginationContainer.innerHTML = '';

        this.images.forEach((_, index) => {
            const circle = document.createElement('button');
            circle.className = `carousel-pagination-dot ${index === 0 ? 'active' : ''}`;
            circle.setAttribute('type', 'button');
            circle.setAttribute('aria-label', `Go to image ${index + 1}`);

            circle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                this.goToImage(index);
                this.stopAutoScroll();
            });

            this.paginationContainer.appendChild(circle);
        });
    }

    showImage(index) {
        if (index < 0 || index >= this.images.length) return;

        this.currentIndex = index;

        this.images.forEach((img, i) => {
            img.classList.toggle('active', i === index);
        });

        if (this.paginationContainer) {
            const dots = this.paginationContainer.querySelectorAll('.carousel-pagination-dot');
            dots.forEach((dot, i) => {
                dot.classList.toggle('active', i === index);
            });
        }
    }

    nextImage() {
        const nextIndex = (this.currentIndex + 1) % this.images.length;
        this.showImage(nextIndex);
    }

    goToImage(index) {
        this.showImage(index);
    }

    startAutoScroll() {
        if (this.autoScrollInterval) return;

        this.isAutoScrolling = true;

        if (this.track) {
            this.track.classList.remove('paused');
        }

        this.autoScrollInterval = setInterval(() => {
            this.nextImage();
        }, this.autoScrollDuration);
    }

    stopAutoScroll() {
        if (!this.isAutoScrolling) return;

        this.isAutoScrolling = false;

        if (this.track) {
            this.track.classList.add('paused');
        }

        if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
        }
    }

    toggleAutoScroll() {
        if (this.isAutoScrolling) {
            this.stopAutoScroll();
        } else {
            this.startAutoScroll();
        }
    }

    destroy() {
        this.stopAutoScroll();

        if (this.track) {
            this.track.removeEventListener('click', this.onTrackClick);
        }

        if (this.container) {
            delete this.container.dataset.carouselInitialized;
        }
    }
}

export default ImageCarousel;
